import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { sdkRequest } from '@eventbrite/http';
import React, { useContext, useEffect, useState } from 'react';
import { EventBasicInformationContext } from '../../contexts/EventContext';
import { dynaMount } from '../utils/mountComponent';
import {
    excludeDuplicatedEvents,
    filterEvents,
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
    NUMBER_OF_MORE_EVENTS_TO_SHOW,
} from './api';
import { MORE_EVENTS_FROM_ID, RELATED_EVENTS_ID } from './constants';
import MoreEventsFrom from './MoreEventsFrom';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';
import {
    EventsFromCollectionResponse,
    transformCollectionEvent,
} from './transform';
import { RelatedEventsProps } from './types';

const eventClient = new EventClient();

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId'
> & {
    isSingleCreatorCollection?: boolean;
    collectionUrl?: string;
};

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreEventsFromMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFrom,
            containerId: MORE_EVENTS_FROM_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const { organizerId, eventId, creatorCollections, isAuthenticated } = props;

    const isSingleCreatorCollection = creatorCollections?.length === 1;
    const collectionUrl =
        (creatorCollections && creatorCollections[0]?.relative_url) ||
        undefined;
    const collectionId = creatorCollections && creatorCollections[0]?.id;

    const relatedEvents = useRelatedEvents(
        organizerId,
        eventId,
        isAuthenticated,
        creatorCollections?.length,
        collectionId,
    );

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                {relatedEvents.eventsSameOrganizer && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameOrganizer || []}
                    />
                )}
                {relatedEvents.eventsSameCollection && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameCollection || []}
                        isSingleCreatorCollection={isSingleCreatorCollection}
                        collectionUrl={collectionUrl}
                    />
                )}
                {relatedEvents.eventsOtherOrganizers && (
                    <OtherEventsYouMayLikeMount
                        {...props}
                        events={
                            relatedEvents.eventsOtherOrganizers.filteredEvents
                        }
                        promotedEvents={
                            relatedEvents.eventsOtherOrganizers.promotedEvents
                        }
                    />
                )}
            </div>
        </EventClientProvider>
    );
};

export interface EventsOtherOrganizers {
    filteredEvents: FormattedEvent[];
    promotedEvents: FormattedEvent[];
}

export const useRelatedEvents = (
    organizerId: string,
    eventId: string,
    isAuthenticated: boolean,
    collectionsLength?: number,
    collectionId?: string,
    isSalesEndedListingExperimentEnabled?: boolean,
) => {
    const [eventsSameOrganizer, setEventsSameOrganizer] =
        useState<FormattedEvent[]>();
    const [eventsSameCollection, setEventsSameCollection] =
        useState<FormattedEvent[]>();
    const [eventsOtherOrganizers, setEventsOtherOrganizers] =
        useState<EventsOtherOrganizers>();

    const eventBasicInformation = useContext(EventBasicInformationContext);

    useEffect(() => {
        if (!eventBasicInformation) {
            return;
        }

        const fetchEventsSameOrganizer = () => {
            getOrganizerRelatedEventsFromDestination(organizerId, eventId)
                .then((response) => {
                    if (response) {
                        const { events } = response;
                        setEventsSameOrganizer(events);
                        return events;
                    }
                })
                .then((eventsSameOrganizer) => {
                    return fetchEventsOtherOrganizers(eventsSameOrganizer);
                });
        };

        const fetchEventsSameCollection = () => {
            sdkRequest(
                `/collections/${collectionId}/events/public?time_filter=current_future`,
            )
                .then((response: EventsFromCollectionResponse) => {
                    const filteredEvents = filterEvents(
                        NUMBER_OF_MORE_EVENTS_TO_SHOW,
                        response.events,
                        eventId,
                    );
                    const transformedEvents = filteredEvents.map(
                        transformCollectionEvent,
                    );
                    setEventsSameCollection(transformedEvents);
                    return transformedEvents;
                })
                .then(fetchEventsOtherOrganizers);
        };

        const fetchEventsOtherOrganizers = (eventsFrom?: FormattedEvent[]) => {
            const _getRelatedEventsOtherOrganizersApi = async (
                eventsFromIds: string[] = [],
            ) => {
                const relatedEvents = await getRelatedEvents(
                    isAuthenticated,
                    eventBasicInformation,
                );
                const promotedEvents = relatedEvents.promotedEvents || [];

                const filteredEvents =
                    eventsFromIds.length > 0
                        ? excludeDuplicatedEvents(
                              relatedEvents.events,
                              eventsFromIds,
                          )
                        : relatedEvents.events;

                return {
                    filteredEvents,
                    promotedEvents,
                };
            };

            const shouldShowRelatedEventsLive =
                document.getElementById(RELATED_EVENTS_ID);

            const eventsFromIds: string[] | undefined = eventsFrom?.map(
                (event) => event.id,
            );

            if (
                shouldShowRelatedEventsLive ||
                isSalesEndedListingExperimentEnabled
            ) {
                _getRelatedEventsOtherOrganizersApi(eventsFromIds).then(
                    setEventsOtherOrganizers,
                );
            }
        };

        if (collectionsLength === 0) {
            fetchEventsSameOrganizer();
        } else if (collectionsLength === 1) {
            fetchEventsSameCollection();
        } else if (collectionsLength && collectionsLength >= 1) {
            fetchEventsOtherOrganizers();
        }
    }, [
        collectionId,
        collectionsLength,
        eventBasicInformation,
        eventId,
        organizerId,
        isAuthenticated,
        isSalesEndedListingExperimentEnabled,
    ]);

    return { eventsSameOrganizer, eventsSameCollection, eventsOtherOrganizers };
};
export default RelatedEventsWrapper;
